import { useState } from "react";
import {
  Smartphone,
  ScanLine,
  ListTodo,
  ClipboardList,
  Warehouse,
  RefreshCw,
  Building2,
  Calendar,
  Sparkles,
  ChevronRight,
  ShieldCheck,
  Zap,
  MousePointerClick,
  Compass,
  CheckCircle2,
  Bell,
  Home,
  BarChart3,
  Layers,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import {
  Avatar,
  Badge,
  Banner,
  BottomNavigation,
  Button,
  Card,
  MobileHeader,
  StickyActionBar,
} from "@/shared/ui/core";
import { FieldTaskCard } from "@/modules/mes/components/FieldTaskCard";
import { FieldTodayKpiStrip } from "@/modules/mes/components/FieldTodayKpiStrip";
import { PassFailControl, type PassFailValue } from "@/modules/mes/components/capture/PassFailControl";
import { NumberStepper } from "@/modules/mes/components/capture/NumberStepper";

export function TestUiMobileSection() {
  // State for Bottom Navigation demo
  const [activeBottomNav, setActiveBottomNav] = useState("home");
  const [activeBottomNav5, setActiveBottomNav5] = useState("today");

  // State for KPI Strip demo
  const [selectedKpi, setSelectedKpi] = useState<string | null>("inProgress");

  // State for Capture Controls demo
  const [passFailVal, setPassFailVal] = useState<PassFailValue>("Pass");
  const [stepperVal, setStepperVal] = useState("24.5");

  const bottomNav4Items = [
    { id: "home", label: "Trang chủ", route: "#home", icon: "home" as const },
    { id: "scan", label: "Quét", route: "#scan", icon: "scan-line" as const },
    { id: "cip", label: "CIP", route: "#cip", icon: "clipboard-list" as const },
    { id: "account", label: "Tài khoản", route: "#account", icon: "user" as const },
  ];

  const bottomNav5Items = [
    { id: "today", label: "Hàng đợi", route: "#today", icon: "list-todo" as const },
    { id: "scan", label: "Quét", route: "#scan", icon: "scan-line" as const },
    { id: "cip", label: "CIP", route: "#cip", icon: "clipboard-list" as const },
    { id: "wms", label: "Kho sàn", route: "#wms", icon: "warehouse" as const },
    { id: "profile", label: "Cá nhân", route: "#profile", icon: "user" as const },
  ];

  const demoKpis = [
    { id: "pending", label: "Chờ", value: "100", tone: "draft" as const },
    { id: "inProgress", label: "Đang chạy", value: "8", tone: "info" as const },
    { id: "paused", label: "Tạm dừng", value: "0", tone: "warning" as const },
    { id: "blocked", label: "Đang giữ", value: "0", tone: "danger" as const },
    { id: "completed", label: "Xong", value: "20", tone: "primary" as const },
    { id: "skipped", label: "Bỏ qua", value: "0", tone: "draft" as const },
    { id: "cancelled", label: "Hủy", value: "0", tone: "danger" as const },
  ];

  const demoShortcuts = [
    {
      label: "Hàng đợi sản xuất",
      detail: "46 việc chờ nhận",
      icon: <ListTodo className="size-5" strokeWidth={2} absoluteStrokeWidth />,
      iconBgClass: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      badge: "46",
      badgeClass: "bg-emerald-soft text-emerald-fg",
    },
    {
      label: "Quét mã Barcode / QR",
      detail: "Nhận diện lô · Pallet",
      icon: <ScanLine className="size-5" strokeWidth={2} absoluteStrokeWidth />,
      iconBgClass: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    },
    {
      label: "Vệ sinh đường ống CIP",
      detail: "1 quy trình đang chạy",
      icon: <ClipboardList className="size-5" strokeWidth={2} absoluteStrokeWidth />,
      iconBgClass: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
      badge: "1 chạy",
      badgeClass: "bg-warning-soft text-warning-fg",
    },
    {
      label: "Kho hiện trường (WMS)",
      detail: "Điều chuyển · Kiểm kê",
      icon: <Warehouse className="size-5" strokeWidth={2} absoluteStrokeWidth />,
      iconBgClass: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Intro Header */}
      <div className="flex flex-col gap-3 rounded-2xl border border-border/80 bg-surface p-6 shadow-xs md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Smartphone className="size-5" />
            </div>
            <h2 className="m-0 text-h2 font-bold text-text">
              8. Mobile & Field Core Components
            </h2>
            <Badge tone="accent" label="Core Primitives & Patterns" />
          </div>
          <p className="m-0 mt-2 text-body text-text-muted max-w-3xl">
            Các thành phần giao diện dùng chung chuẩn hóa cho thiết bị di động, máy quét công nghiệp cầm tay (Handheld/PDA) và môi trường hiện trường sản xuất (Field Shell & Exe Shell).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="success" label="Chuẩn Touch Target ≥ 44px" />
          <Badge tone="info" label="Thumb Zone Optimization" />
        </div>
      </div>

      {/* Component 1: MobileHeader */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Compass className="size-5 text-primary" />
              1. Header Cảm Ứng Di Động (MobileHeader)
            </h3>
            <Badge tone="primary" label="@/shared/ui/core/MobileHeader" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Header dính cố định đỉnh màn hình (`sticky top-0`) có hỗ trợ `safe-area-inset-top`, nút back cảm ứng, tiêu đề căn giữa và vùng hành động bên phải.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Variant A: tint="field" */}
          <div className="rounded-xl border border-border/70 overflow-hidden bg-bg">
            <div className="bg-surface px-4 py-2 border-b border-border/60 flex items-center justify-between">
              <span className="text-caption font-bold text-text">
                Variant Field: <code className="text-primary font-mono">tint="field"</code> (Xanh đậm Nhà máy)
              </span>
              <Badge tone="success" label="Field Shell" />
            </div>
            <div className="p-3">
              <div className="rounded-lg overflow-hidden border border-primary shadow-sm">
                <MobileHeader
                  title="Trang chủ hiện trường"
                  tint="field"
                  showBack={false}
                  trailing={
                    <button
                      type="button"
                      className="p-2 text-on-primary hover:bg-white/15 rounded-full cursor-pointer relative"
                    >
                      <Bell className="size-5" />
                      <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-red-500" />
                    </button>
                  }
                />
              </div>
            </div>
          </div>

          {/* Variant B: tint="default" with Back button */}
          <div className="rounded-xl border border-border/70 overflow-hidden bg-bg">
            <div className="bg-surface px-4 py-2 border-b border-border/60 flex items-center justify-between">
              <span className="text-caption font-bold text-text">
                Variant Standard: <code className="text-primary font-mono">tint="default"</code> (Surface Chrome)
              </span>
              <Badge tone="neutral" label="Exe / Child screens" />
            </div>
            <div className="p-3">
              <div className="rounded-lg overflow-hidden border border-border shadow-sm">
                <MobileHeader
                  title="Chi tiết công đoạn"
                  tint="default"
                  showBack={true}
                  onBack={() => alert("Nút quay lại (onBack) được kích hoạt")}
                  trailing={
                    <Button size="sm" variant="secondary" className="text-xs">
                      Lưu
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Component 2: BottomNavigation */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Compass className="size-5 text-primary" />
              2. Thanh Điều Hướng Đáy Ngón Cái (BottomNavigation)
            </h3>
            <Badge tone="primary" label="@/shared/ui/core/BottomNavigation" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Thanh điều hướng cố định phía dưới màn hình (`fixed inset-x-0 bottom-0`), căn đều các tab cảm ứng. Trạng thái active dùng màu thương hiệu `text-primary` và font đậm (đã loại bỏ border xanh trên icon).
          </p>
        </div>

        <div className="space-y-4">
          {/* Demo 1: 4 Tabs */}
          <div className="rounded-xl border border-border/70 overflow-hidden bg-bg">
            <div className="bg-surface px-4 py-2 border-b border-border/60 flex items-center justify-between">
              <span className="text-caption font-bold text-text">
                Cấu hình 4 Tab (Field Shell mặc định)
              </span>
              <span className="text-caption text-text-muted">
                Đang chọn: <strong className="text-primary font-mono">{activeBottomNav}</strong>
              </span>
            </div>
            <div className="p-4">
              <div className="relative overflow-hidden rounded-xl border border-border bg-surface shadow-sm max-w-md mx-auto">
                <BottomNavigation
                  className="static"
                  items={bottomNav4Items}
                  activeId={activeBottomNav}
                />
              </div>
              <div className="mt-3 flex justify-center gap-2">
                {bottomNav4Items.map((item) => (
                  <Button
                    key={item.id}
                    size="sm"
                    variant={activeBottomNav === item.id ? "primary" : "secondary"}
                    onClick={() => setActiveBottomNav(item.id)}
                  >
                    Chọn {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Demo 2: 5 Tabs */}
          <div className="rounded-xl border border-border/70 overflow-hidden bg-bg">
            <div className="bg-surface px-4 py-2 border-b border-border/60 flex items-center justify-between">
              <span className="text-caption font-bold text-text">
                Cấu hình 5 Tab (Mở rộng phân xưởng)
              </span>
              <span className="text-caption text-text-muted">
                Đang chọn: <strong className="text-primary font-mono">{activeBottomNav5}</strong>
              </span>
            </div>
            <div className="p-4">
              <div className="relative overflow-hidden rounded-xl border border-border bg-surface shadow-sm max-w-lg mx-auto">
                <BottomNavigation
                  className="static"
                  items={bottomNav5Items}
                  activeId={activeBottomNav5}
                />
              </div>
              <div className="mt-3 flex justify-center gap-2">
                {bottomNav5Items.map((item) => (
                  <Button
                    key={item.id}
                    size="sm"
                    variant={activeBottomNav5 === item.id ? "primary" : "secondary"}
                    onClick={() => setActiveBottomNav5(item.id)}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Component 3: FieldTaskCard */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Layers className="size-5 text-primary" />
              3. Thẻ Công Việc Hiện Trường (FieldTaskCard)
            </h3>
            <Badge tone="primary" label="@/modules/mes/components/FieldTaskCard" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Thẻ công đoạn sản xuất dành cho thiết bị cảm ứng: vạch màu phân biệt trạng thái ở cạnh trái (`border-l-4`), các mã LSX và Mẻ dạng chip tag monospaced, và nút hành động to rõ.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Card 1: InProgress */}
          <div className="space-y-1.5">
            <span className="text-caption font-bold text-accent-fg uppercase tracking-wider px-1">
              • Đang chạy (InProgress) — Vạch xanh lá, nút "Tiếp tục"
            </span>
            <FieldTaskCard
              to="#demo-running"
              status="InProgress"
              workContext={{
                operationName: "Tiếp nhận NVL lên chuyền",
                operationCode: "OP-RECV-LINE",
                operationStatus: "InProgress",
                productName: "Sinh tố việt quất cao cấp · xô 20kg",
                orderNo: "MO-STVQ-20260713",
                batchCode: "B-STVQ-DEMO-02",
              }}
            />
          </div>

          {/* Card 2: Blocked */}
          <div className="space-y-1.5">
            <span className="text-caption font-bold text-hold-fg uppercase tracking-wider px-1">
              • Bị chặn (Blocked) — Vạch đỏ cảnh báo, nút "Xem chi tiết"
            </span>
            <FieldTaskCard
              to="#demo-blocked"
              status="Blocked"
              workContext={{
                operationName: "Đồng hóa áp suất cao",
                operationCode: "OP-HOMO-HIGH",
                operationStatus: "Blocked",
                productName: "Sốt dâu tây thượng hạng · chai 1L",
                orderNo: "MO-20260929-E50C09",
                batchCode: "B-20260929-01",
              }}
            />
          </div>

          {/* Card 3: Paused */}
          <div className="space-y-1.5">
            <span className="text-caption font-bold text-warning-fg uppercase tracking-wider px-1">
              • Tạm dừng (Paused) — Vạch vàng cảnh báo, nút "Tiếp tục"
            </span>
            <FieldTaskCard
              to="#demo-paused"
              status="Paused"
              workContext={{
                operationName: "Chiết rót vô trùng túi",
                operationCode: "OP-FILL-ASEPTIC",
                operationStatus: "Paused",
                productName: "Nước ép cam nha đam · hộp 250ml",
                orderNo: "MO-20261001-C01",
                batchCode: "B-CAM-01",
              }}
            />
          </div>

          {/* Card 4: Pending */}
          <div className="space-y-1.5">
            <span className="text-caption font-bold text-text-muted uppercase tracking-wider px-1">
              • Chờ xử lý (Pending) — Vạch trung tính, nút "Bắt đầu"
            </span>
            <FieldTaskCard
              to="#demo-pending"
              status="Pending"
              workContext={{
                operationName: "Thanh trùng nhanh UHT",
                operationCode: "OP-PAST-UHT",
                operationStatus: "Pending",
                productName: "Sữa chua uống việt quất · lốc 4",
                orderNo: "MO-20261002-U02",
                batchCode: "B-SC-DEMO",
              }}
            />
          </div>
        </div>
      </section>

      {/* Component 4: FieldTodayKpiStrip */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Zap className="size-5 text-primary" />
              4. Thẻ Chỉ Số Xúc Giác Hàng Đợi (FieldTodayKpiStrip)
            </h3>
            <Badge tone="primary" label="@/modules/mes/components/FieldTodayKpiStrip" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Thanh 4 thẻ chỉ số trạng thái công đoạn có tone màu riêng (Đỏ, Xanh, Vàng, Xám), hỗ trợ phản hồi xúc giác `active:scale-95` khi chạm ngón tay.
          </p>
        </div>

        <div className="space-y-4 max-w-xl mx-auto">
          <FieldTodayKpiStrip
            metrics={demoKpis}
            activeId={selectedKpi}
            onSelect={(id) => setSelectedKpi(id)}
          />

          <div className="text-center text-caption text-text-muted">
            Trạng thái đang được chọn thử nghiệm:{" "}
            <span className="font-bold text-primary font-mono">{selectedKpi ?? "Chưa chọn"}</span>
          </div>
        </div>
      </section>

      {/* Component 5: Lưới Thẻ Phím Tắt Tác Nghiệp (Action Tiles) */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <MousePointerClick className="size-5 text-primary" />
              5. Lưới Thẻ Phím Tắt Tác Nghiệp (Mobile Action Tiles)
            </h3>
            <Badge tone="accent" label="Pattern Lưới 2 Cột" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Thay thế danh sách 1 cột nhỏ chữ bằng lưới 2 cột ngón cái với icon squircle đa màu sắc, chiều cao tối thiểu 60px, badge số lượng trực quan.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {demoShortcuts.map((s) => (
            <div
              key={s.label}
              className="group relative flex flex-col justify-between rounded-2xl border border-border/70 bg-surface p-4 shadow-xs transition-all active:scale-[0.98] hover:border-primary/40 cursor-pointer"
            >
              <div className="flex items-start justify-between gap-2">
                <div
                  className={cn(
                    "flex size-11 items-center justify-center rounded-xl transition-transform group-hover:scale-105",
                    s.iconBgClass,
                  )}
                >
                  {s.icon}
                </div>
                {s.badge ? (
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-bold",
                      s.badgeClass,
                    )}
                  >
                    {s.badge}
                  </span>
                ) : (
                  <ChevronRight className="size-4 text-text-muted transition-transform group-hover:translate-x-0.5" />
                )}
              </div>
              <div className="mt-4">
                <span className="block text-body font-bold text-text group-hover:text-primary transition-colors">
                  {s.label}
                </span>
                <span className="block truncate text-caption text-text-muted mt-0.5 font-medium">
                  {s.detail}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Component 6: Mobile Hero Identity Card */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Sparkles className="size-5 text-primary" />
              6. Thẻ Danh Tính & Quét Nhanh (Mobile Hero Card)
            </h3>
            <Badge tone="primary" label="Pattern Hero Hiện Trường" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Đặt tại đầu màn hình để xác nhận tài khoản công nhân/QC, Nhà máy, ngày ca làm việc và nút Quét mã nhanh trong tầm tay ngón cái.
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="rounded-2xl border border-border/70 bg-surface p-4 shadow-xs">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar
                  name="QC Plant1"
                  size="md"
                  status="online"
                  shape="rounded"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-caption text-text-muted">Xin chào,</span>
                    <span className="m-0 truncate text-body font-bold text-text">
                      QC Plant1
                    </span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-caption text-text-muted">
                    <span className="inline-flex items-center gap-1 font-medium text-text-secondary">
                      <Building2 className="size-3.5 text-primary" />
                      <span>Plant PLANT-01</span>
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="size-3.5" />
                      <span>Thứ 2, 05/10</span>
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0 rounded-xl font-bold shadow-xs active:scale-95"
                leftIcon={<ScanLine className="size-4 text-primary" />}
                onClick={() => alert("Mở camera quét mã Barcode / QR")}
              >
                Quét
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Component 7: PassFailControl */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <CheckCircle2 className="size-5 text-primary" />
              7. Điều Khiển Đạt / Không Đạt (PassFailControl)
            </h3>
            <Badge tone="primary" label="@/modules/mes/components/capture/PassFailControl" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            2 nút bấm lớn cảm ứng Đạt (Pass) / Không đạt (Fail), thay thế Radio nút nhỏ khi thao tác kiểm tra ngoại quan, QC checksheet hoặc HACCP hiện trường.
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-4 p-4 rounded-xl border border-border/70 bg-bg">
          <PassFailControl
            label="1. Kiểm tra cảm quan & màu sắc mẻ sấy (HACCP CCP-1)"
            value={passFailVal}
            onChange={setPassFailVal}
          />

          <div className="flex items-center justify-between text-caption text-text-muted pt-2 border-t border-border/60">
            <span>Trạng thái giá trị:</span>
            <Badge
              tone={passFailVal === "Pass" ? "success" : passFailVal === "Fail" ? "danger" : "neutral"}
              label={passFailVal ?? "Chưa chọn"}
            />
          </div>
        </div>
      </section>

      {/* Component 8: NumberStepper */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <Zap className="size-5 text-primary" />
              8. Ô Nhập Số Bước Nhảy Cảm Ứng (NumberStepper)
            </h3>
            <Badge tone="primary" label="@/modules/mes/components/capture/NumberStepper" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Ô nhập số tích hợp hai nút bấm (-) và (+) kích thước `size="touch"`, giúp công nhân tăng/giảm nhanh giá trị số đo (nhiệt độ, trọng lượng, áp suất) mà không cần mở bàn phím ảo.
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-4 p-4 rounded-xl border border-border/70 bg-bg">
          <NumberStepper
            label="Nhiệt độ gia nhiệt thực tế"
            uom="°C"
            step={0.5}
            value={stepperVal}
            onChange={setStepperVal}
            hint="Dải cho phép: 20°C - 30°C · Bước nhảy: ±0.5°C"
          />

          <div className="flex items-center justify-between text-caption text-text-muted pt-2 border-t border-border/60">
            <span>Giá trị hiện tại:</span>
            <span className="font-mono font-bold text-primary">{stepperVal} °C</span>
          </div>
        </div>
      </section>

      {/* Component 9: StickyActionBar */}
      <section className="rounded-2xl border border-border bg-surface p-6 shadow-xs space-y-4">
        <div className="border-b border-border/60 pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary" />
              9. Thanh Thao Tác Dính Đáy (StickyActionBar - Glove UX)
            </h3>
            <Badge tone="primary" label="@/shared/ui/core/StickyActionBar" />
          </div>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Thanh hành động chính cố định phía dưới màn hình (dính ngay trên BottomNavigation hoặc đáy viewport), nút to phục vụ thao tác bấm 1 tay hoặc khi đeo găng tay bảo hộ trong phân xưởng.
          </p>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border/70 overflow-hidden bg-bg p-4 max-w-lg mx-auto">
            <div className="text-caption font-semibold text-text-muted uppercase tracking-wider mb-2">
              Mô phỏng thanh dính (Preview static in container):
            </div>
            <div className="relative overflow-hidden rounded-xl border border-border bg-surface p-3 shadow-md">
              <div className="flex items-center gap-3">
                <Button
                  size="touch"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => alert("Hành động phụ: Tạm dừng / Từ chối")}
                >
                  Tạm dừng
                </Button>
                <Button
                  size="touch"
                  variant="primary"
                  className="flex-[2] font-bold"
                  leftIcon={<ArrowRight className="size-5" />}
                  onClick={() => alert("Hành động chính: Hoàn tất công đoạn")}
                >
                  Hoàn tất công đoạn
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
